import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, XCircle, Hash, Calendar, FileText, ArrowLeft, ExternalLink } from "lucide-react";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";

/**
 * VerifyHashPage. public, no-auth route at /verify/:hash
 *
 * Anyone with a SHA-256 hash from a Photo Trail photo can check whether it
 * was uploaded to TenantTrail and (if so) when. The endpoint returns NO
 * identifying info. just bytes-existed-at-T proof. Designed to be shown
 * to a tribunal adjudicator or opposing party as chain-of-custody evidence.
 */
export default function VerifyHashPage() {
 const { hash } = useParams();
 const [result, setResult] = useState(null);
 const [error, setError] = useState(null);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 let alive = true;
 api.get(`/verify/${hash}`)
.then(({ data }) => alive && setResult(data))
.catch(e => alive && setError(e.response?.data?.detail || "Verification lookup failed"))
.finally(() => alive && setLoading(false));
 }, [hash]);

 const cleanHash = (hash || "").toLowerCase();

 return (
 <div className="min-h-screen paper-grain flex flex-col">
 <Navbar />
 <div className="flex-1 max-w-2xl mx-auto px-6 sm:px-8 py-10 w-full">
 <Link to="/" className="btn-ghost inline-flex items-center gap-1.5 text-sm mb-6" data-testid="verify-back-home">
 <ArrowLeft size={14} strokeWidth={1.5} /> Back to TenantTrail
 </Link>

 <div className="label-mono text-[#1B6C62] mb-2 flex items-center gap-1.5">
 <ShieldCheck size={11} strokeWidth={1.75} />
 <span>Photo check</span>
 </div>
 <h1 className="font-serif text-3xl sm:text-4xl text-[#0D2B52] mb-2 leading-tight">
 Photo verification
 </h1>
 <p className="text-[14px] text-[#5a6b7a] mb-6 leading-relaxed">
 This page checks whether a photo with this unique fingerprint was
 saved on TenantTrail. If yes, the date and time below is when our
 system first received it, a solid record of when the photo existed,
 for anyone who needs to see it.
 </p>

 <div className="card mb-6">
 <div className="label-mono text-[10px] text-[#5a6b7a] mb-1">Photo fingerprint</div>
 <code className="block break-all text-[12px] sm:text-[13px] font-mono text-[#1A1A1A] bg-[#EFEDE5] p-3" data-testid="verify-hash-display">
 {cleanHash}
 </code>
 </div>

 {loading && (
 <div className="card text-center py-8 text-[#5a6b7a] italic">
 Checking our records…
 </div>
 )}

 {!loading && error && (
 <div className="card border-l-4 border-l-[#B35D4C]" data-testid="verify-error">
 <div className="flex items-center gap-2 text-[#B35D4C] mb-1">
 <XCircle size={16} strokeWidth={1.5} />
 <span className="font-serif text-lg">Lookup failed</span>
 </div>
 <p className="text-[13px] text-[#1A1A1A]">{error}</p>
 </div>
 )}

 {!loading && result && !result.verified && (
 <div className="card border-l-4 border-l-[#B35D4C]" data-testid="verify-not-found">
 <div className="flex items-center gap-2 text-[#B35D4C] mb-2">
 <XCircle size={18} strokeWidth={1.5} />
 <span className="font-serif text-xl">Not in our system</span>
 </div>
 <p className="text-[13px] text-[#1A1A1A] leading-relaxed mb-3">
 No file with that SHA-256 hash has been uploaded to TenantTrail.
 That doesn't mean the photo is fake. It just means it wasn't
 stored here.
 </p>
 <p className="text-[12px] text-[#5a6b7a]">
 If you're trying to verify a photo you took, double-check the
 hash. SHA-256 hashes are 64 hex characters (0-9, a-f).
 </p>
 </div>
 )}

 {!loading && result && result.verified && (
 <div className="card border-l-4 border-l-[#1B6C62]" data-testid="verify-success">
 <div className="flex items-center gap-2 text-[#1B6C62] mb-3">
 <ShieldCheck size={20} strokeWidth={1.5} />
 <span className="font-serif text-xl">Verified</span>
 </div>
 <p className="text-[14px] text-[#1A1A1A] leading-relaxed mb-5">
 A photo with this fingerprint was saved to TenantTrail at the
 time shown below. The fingerprint is unique to those exact bytes
, if the photo is altered in any way, the fingerprint
 changes too.
 </p>

 <div className="grid sm:grid-cols-2 gap-4 mb-5">
 <div>
 <div className="label-mono text-[10px] text-[#5a6b7a] mb-0.5 flex items-center gap-1">
 <Calendar size={10} strokeWidth={1.75} /> Uploaded (UTC)
 </div>
 <div className="font-mono text-[14px] text-[#0D2B52]" data-testid="verify-uploaded-at">
 {new Date(result.uploaded_at_utc).toUTCString()}
 </div>
 </div>
 <div>
 <div className="label-mono text-[10px] text-[#5a6b7a] mb-0.5 flex items-center gap-1">
 <FileText size={10} strokeWidth={1.75} /> File
 </div>
 <div className="font-mono text-[13px] text-[#0D2B52] break-all">
 {result.filename || ","}
 </div>
 <div className="text-[11px] text-[#5a6b7a] mt-0.5">
 {result.mime_type} · {(result.file_size_bytes / 1024).toFixed(1)} KB
 </div>
 </div>
 </div>

 <div className="border-t border-[#E5E4DE] pt-4">
 <div className="label-mono text-[10px] text-[#5a6b7a] mb-1">Statement for tribunal</div>
 <blockquote className="text-[13px] font-serif italic text-[#1A1A1A] bg-[#EFEDE5] p-3 border-l-2 border-[#1B6C62]">
 {result.statement}
 </blockquote>
 </div>
 </div>
 )}

 <div className="mt-8 border-t border-[#E5E4DE] pt-6">
 <h3 className="font-serif text-lg text-[#0D2B52] mb-2">What is this?</h3>
 <p className="text-[13px] text-[#5a6b7a] leading-relaxed mb-3">
 TenantTrail&rsquo;s <strong>Photo Trail</strong> keeps a friendly
 monthly photo log of a renter&rsquo;s home. Every photo gets a
 unique fingerprint and a save date. This page lets anyone, the
 agent, a lawyer, anyone, quickly check that the same photo
 existed in our system at the time shown.
 </p>
 <p className="text-[13px] text-[#5a6b7a] leading-relaxed">
 So if anyone claims a photo was taken on a different day, the
 record tells the real story.
 </p>
 <Link to="/pricing" className="inline-flex items-center gap-1 text-[13px] text-[#1B6C62] hover:opacity-80 mt-3" data-testid="verify-learn-more">
 Learn more about Photo Trail <ExternalLink size={11} strokeWidth={1.75} />
 </Link>
 </div>
 </div>
 </div>
 );
}
